import type { Ref } from 'react'
import Immutable from 'immutable'

import LogoCollection from './LogoCollection'

type Props = {
  updatePopoverPosition: VoidFunction
  fieldsetRef: Ref<HTMLFieldSetElement>
}

export default function PaymentSettings({ updatePopoverPosition, fieldsetRef }: Readonly<Props>) {
  return <LogoCollection scope="paymentLogos" updatePopoverPosition={updatePopoverPosition} fieldsetRef={fieldsetRef} />
}

PaymentSettings.defaultValue = ({ shopResellerName, taxCountry }): ImmutableList => {
  const paymentLogoIds: PaymentLogoIds =
    resellerPaymentLogoIds[shopResellerName]?.[taxCountry] ||
    (resellerPaymentLogoIds[shopResellerName] as LanguagePaymentLogoIds)?.all ||
    resellerPaymentLogoIds[shopResellerName] ||
    defaultPaymentLogoIds

  return Immutable.fromJS(
    paymentLogoIds.map(function (id) {
      return {
        id,
        title: paymentLogoTitles[id],
        active: false,
      }
    }),
  )
}

const paymentLogoTitles = {
  directDebit: null,
  invoice: null,
  payInAdvance: null,
  maestro: 'Maestro',
  visa: 'Visa',
  paypal: 'PayPal',
  americanExpress: 'American Express',
  klarna: 'Klarna',
  mastercard: 'MasterCard',
  applePay: 'Apple Pay',
  googlePay: 'Google Pay',
  amazonPay: 'Amazon Pay',
  redsys: 'Redsys',
  giropay: 'Giropay',
  vpay: 'VPay',
  diners: 'Diners',
  discover: 'Discover',
  payNow: 'PayNow (SOFORT)',
  bancontact: 'Bancontact',
  ideal: 'iDEAL',
  eps: 'EPS',
  twint: 'TWINT',
  postFinanceCard: 'PostFinance Card',
  postFinanceEfinance: 'PostFinance eFinance',
}

const defaultPaymentLogoIds: Partial<PaymentLogoIds> = [
  'directDebit',
  'invoice',
  'payInAdvance',
  'maestro',
  'visa',
  'paypal',
  'americanExpress',
  'klarna',
  'mastercard',
  'applePay',
  'googlePay',
  'amazonPay',
  'redsys',
  'giropay',
]

type PaymentLogoIds = (keyof typeof paymentLogoTitles)[]
type LanguagePaymentLogoIds = { [language: string]: PaymentLogoIds }
type ResellerPaymentLogoIds = {
  [resellerId: string]: PaymentLogoIds | LanguagePaymentLogoIds
}

// Define reseller specific payment logos, based on the table on this page in Confluence:
// https://epages.atlassian.net/wiki/spaces/BEYOND/pages/933625896/Reseller+specific+payment+logos
const resellerPaymentLogoIds: ResellerPaymentLogoIds = {
  payone: ['paypal', 'visa', 'mastercard', 'directDebit', 'invoice', 'payInAdvance', 'giropay'],
  'bspayone-sp': ['paypal', 'visa', 'mastercard', 'directDebit', 'invoice', 'payInAdvance', 'giropay'],
  vrpayment: [
    'directDebit',
    'invoice',
    'payInAdvance',
    'maestro',
    'visa',
    'paypal',
    'americanExpress',
    'klarna',
    'mastercard',
    'amazonPay',
    'redsys',
  ],
  worldlinesix: {
    CH: [
      'visa',
      'vpay',
      'mastercard',
      'maestro',
      'diners',
      'discover',
      'applePay',
      'twint',
      'americanExpress',
      'payNow',
      'paypal',
      'postFinanceCard',
      'postFinanceEfinance',
    ],
    DE: [
      'visa',
      'vpay',
      'mastercard',
      'maestro',
      'diners',
      'discover',
      'applePay',
      'americanExpress',
      'payNow',
      'paypal',
    ],
    AT: [
      'visa',
      'vpay',
      'mastercard',
      'maestro',
      'diners',
      'discover',
      'applePay',
      'americanExpress',
      'payNow',
      'paypal',
      'eps',
    ],
    all: ['visa', 'vpay', 'mastercard', 'maestro', 'diners', 'discover', 'payNow', 'paypal', 'americanExpress'],
  },
}
